import { Mail, Phone, Globe } from "lucide-react";
import PolicyLayout, { Blocks, H2 } from "./PolicyLayout";

// Account & Data Deletion — text supplied verbatim by Iradium Automobiles Pvt Ltd
// (see /app/memory/mobile_specs/… for the source doc). Do not paraphrase; if
// content changes, update this file with the new copy.

const blocks = [
  { type: "p", text: "Users of Fender by SparesHub can request deletion of their Fender account and associated personal data." },
  { type: "p", text: "Fender is operated by:" },
  { type: "callout", text: "Iradium Automobiles Private Limited · Pune, Maharashtra, India" },

  { type: "h2", text: "How to Request Account Deletion" },
  { type: "p", text: "To request deletion of your Fender account, email: contact@spareshub.com" },
  { type: "p", text: "Use the subject: Fender Account Deletion Request" },
  { type: "p", text: "Please include:" },
  { type: "ul", items: [
    "Your name",
    "Registered mobile number",
    "Registered email address, if applicable",
    "Workshop / Garage name",
    "A clear request to delete your Fender account",
  ]},
  { type: "p", text: "We may contact you to verify your identity and authority before processing the request." },

  { type: "h2", text: "What Will Be Deleted" },
  { type: "p", text: "Subject to applicable legal and operational requirements, we may delete or anonymise:" },
  { type: "ul", items: [
    "Fender user account",
    "User profile information",
    "Personal preferences",
    "Login/session information",
    "Eligible workshop-related personal data associated with the account",
  ]},

  { type: "h2", text: "Information That May Be Retained" },
  { type: "p", text: "Certain records may need to be retained where required for:" },
  { type: "ul", items: [
    "Legal or regulatory compliance",
    "Tax and accounting requirements",
    "Fraud prevention",
    "Security investigations",
    "Dispute resolution",
    "Audit history",
    "Outstanding commercial obligations",
    "Establishing or defending legal claims",
  ]},
  { type: "p", text: "Operational records such as Job Cards, Estimates, Invoices, Payments, Gate Passes, inventory transactions or audit records may be retained, archived or anonymised where permanent deletion would affect statutory, commercial or audit records." },

  { type: "h2", text: "Processing Time" },
  { type: "p", text: "Valid deletion requests will be reviewed and processed within the period required under applicable law." },
];

export default function AccountDeletion() {
  return (
    <PolicyLayout
      testId="account-deletion-page"
      title="Account & Data Deletion Request"
      subtitle="How workshop owners and users of Fender by SparesHub can request deletion of their account and associated personal data."
    >
      <Blocks blocks={blocks} />

      {/* Custom contact card per source doc — uses the SparesHub sales
          email + Tapas Gupta phone number, not the generic
          contact@fenderapp.com used by other legal pages. */}
      <H2 id="contact">Contact</H2>
      <div className="bg-[#f1faee] rounded-xl p-6 my-6 border border-[#a8dadc]/40" data-testid="account-deletion-contact">
        <div className="text-[15px] font-bold text-[#1d3557]">
          <span className="notranslate">Iradium Automobiles Private Limited</span>
        </div>
        <div className="text-[13px] text-[#1d3557] mt-1">Pune, Maharashtra, India</div>
        <div className="mt-4 text-[13px] text-[#1d3557]">
          <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#457b9d]">Contact Person</div>
          <div className="mt-1 font-semibold text-[15px] notranslate">Tapas Gupta</div>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[13px]">
          <a href="mailto:contact@spareshub.com" className="inline-flex items-center gap-1.5 text-[#457b9d] hover:text-[#e63946] font-semibold notranslate" data-testid="account-deletion-email">
            <Mail size={13} /> contact@spareshub.com
          </a>
          <a href="tel:+917875523381" className="inline-flex items-center gap-1.5 text-[#457b9d] hover:text-[#e63946] font-semibold notranslate" data-testid="account-deletion-phone">
            <Phone size={13} /> +91 7875523381
          </a>
          <a href="https://fenderapp.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#457b9d] hover:text-[#e63946] font-semibold notranslate">
            <Globe size={13} /> FenderApp.com
          </a>
        </div>
      </div>

      <div className="mt-10 pt-6 border-t border-[#e6edf1] text-[12px] text-[#457b9d] leading-relaxed">
        © 2026 <span className="notranslate">Iradium Automobiles Private Limited</span>. All rights reserved.
        <br />
        <span className="notranslate">Fender by SparesHub</span>
      </div>
    </PolicyLayout>
  );
}
